import React,{useEffect,useState} from 'react';
import {get} from '../../api/coreApi';

type Segment={key:string;share:number;inputTokens:number;outputTokens:number;
  modelId:string|null;provider:string|null;modelTier:string|null;
  estimatedCalls:number;usdPerCall:number|null;monthlyUsd:number|null;
  confidence:string;note?:string};
type Forecast={
  currency:'USD';pricingBasis:string;available:boolean;
  limits:{hourlyPerUser:number;weeklyPerUser:number;monthlyAveragePerUser:number};
  inputs:{users:number;utilizationPct:number;callsPerQuestion:number};
  monthly:{questions:number;estimatedModelCalls:number;estimatedUsd:number|null};
  segments:Segment[];exclusions:string;note:string;
};
const usd=(n:number|null,places=2)=>n===null?'Not available':n.toLocaleString('en-US',{
  style:'currency',currency:'USD',minimumFractionDigits:places,maximumFractionDigits:places,
});
const getLabel=(key:string)=>({lightweight:'Lightweight conversation',operational:'Operational Brain',complex:'Complex reasoning'} as Record<string,string>)[key]||key;
export const EzeeUsageForecastPanel:React.FC<{instanceUniqueId:string|null}>=({instanceUniqueId})=>{
  const [users,setUsers]=useState(10);
  const [utilization,setUtilization]=useState(100);
  const [calls,setCalls]=useState(1);
  const [data,setData]=useState<Forecast|null>(null);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  useEffect(()=>{setData(null);setError('');},[instanceUniqueId,users,utilization,calls]);
  const calculate=async()=>{
    if(!instanceUniqueId)return;
    setLoading(true);setError('');setData(null);
    try{
      const response=await get<{success:boolean;forecast:Forecast}>(
        `/ai/instances/${encodeURIComponent(instanceUniqueId)}/model-routing/forecast`,
        {params:{users,utilizationPct:utilization,callsPerQuestion:calls}},
      );
      setData(response.forecast);
    }catch(cause){
      setError(cause instanceof Error?cause.message:'Unable to load Ezee forecast.');
    }finally{setLoading(false);}
  };
  return <section className="ezee-economics__panel" aria-label="Ezee question allowance and cost forecast">
    <h4>Usage-based cost forecast</h4>
    <p className="ezee-economics__muted">Scenarios use the same per-user hourly and weekly limits as the Brain. Model costs come only from the developer-approved catalog; token sizes and task proportions are illustrative.</p>
    <div className="ezee-economics__forecast-inputs">
      <label>Active users
        <input type="number" value={users} min={1} max={1000} onChange={e=>setUsers(Math.min(1000,Math.max(1,Number(e.target.value)||1)))}/>
      </label>
      <label>Weekly allowance used (%)
        <input type="number" value={utilization} min={0} max={100} step={10}
          onChange={e=>setUtilization(Math.min(100,Math.max(0,Number(e.target.value)||0)))}/>
      </label>
      <label>Model calls per question
        <select value={calls} onChange={e=>setCalls(Number(e.target.value))}>
          {[1,2,3,4,5,6].map(x=><option key={x} value={x}>{x}</option>)}
        </select>
      </label>
      <button type="button" disabled={!instanceUniqueId||loading} onClick={()=>void calculate()}>
        {loading?'Calculating…':'Calculate forecast'}
      </button>
    </div>
    {error?<p role="alert" className="ezee-economics__error">{error}</p>:null}
    {data?<>
      <p className="ezee-economics__scope"><strong>Existing Brain allowance: {data.limits.hourlyPerUser} questions/hour and {data.limits.weeklyPerUser} questions/week per user.</strong>
        {' '}Monthly average allowance: {data.limits.monthlyAveragePerUser.toLocaleString('en-US')} questions per user (52 weeks ÷ 12 months).</p>
      <div className="ezee-economics__metrics">
        <div className="ezee-economics__metric">
          <span>Questions / month</span>
          <strong>{Math.round(data.monthly.questions).toLocaleString('en-US')}</strong>
          <small>Scenario based on weekly allowance</small>
        </div>
        <div className="ezee-economics__metric">
          <span>Billable model calls</span>
          <strong>{Math.round(data.monthly.estimatedModelCalls).toLocaleString('en-US')}</strong>
          <small>Additional calls per user question modeled</small>
        </div>
        <div className="ezee-economics__metric">
          <span>Monthly token spend</span>
          <strong>{usd(data.monthly.estimatedUsd,2)}</strong>
          <small>{data.available?'Illustrative approved model rates':'Approved model pricing incomplete'}</small>
        </div>
      </div>
      <div className="ezee-economics__table-scroll">
        <table className="ezee-economics__table">
          <thead><tr><th>Workload</th><th>Mix</th><th>Token profile</th><th>Estimated model</th><th>Monthly USD</th></tr></thead>
          <tbody>{data.segments.map(row=><tr key={row.key}>
            <td><strong>{getLabel(row.key)}</strong>{row.note?<small>{row.note}</small>:null}</td>
            <td>{Math.round(row.share*100)}%</td>
            <td>{row.inputTokens.toLocaleString('en-US')} in / {row.outputTokens.toLocaleString('en-US')} out</td>
            <td>{row.modelId||'No eligible model'}<small>{row.provider||'—'}</small></td>
            <td>{usd(row.monthlyUsd,4)}</td>
          </tr>)}</tbody>
        </table>
      </div>
      <p className="ezee-economics__muted">Excludes: {data.exclusions} {data.note}</p>
    </>:null}
  </section>;
};
